import { formatarData, formatarInteiro } from '@/lib/formato';
import { combinaComTermo } from '@/lib/texto';

import type {
  ConfiguracaoDaIaDaPlataforma,
  IaEmUso,
  ListaDeModelos,
  ModeloDoProvedor,
  OrigemDaIa,
  ParametrosDaIa,
  ProtocoloDaIa,
  ResultadoDoTesteDaIa,
} from './api';

/**
 * A parte das Configurações do sistema que não depende de tela: o rascunho do
 * motor de IA, a validação que espelha a do servidor, a lista de modelos do
 * provedor e os avisos que ela permite dar ANTES de salvar.
 *
 * Decisão do dono (27/09/2026): o modelo se escolhe aqui, na plataforma; no
 * ambiente fica só a chave (IA_CHAVE). Por isso nada aqui tem campo de chave,
 * e nenhum endereço de provedor está escrito — nem como exemplo (H12).
 */

export const PROTOCOLOS: readonly ProtocoloDaIa[] = ['openai', 'gemini', 'anthropic'];

/** O protocolo em frase curta, para o que está em uso. */
export const ROTULO_DO_PROTOCOLO: Readonly<Record<ProtocoloDaIa, string>> = {
  openai: 'Compatível com OpenAI',
  gemini: 'Gemini',
  anthropic: 'Anthropic',
};

/** O protocolo como opção da escolha, com a pista de quem o fala. */
export const OPCAO_DO_PROTOCOLO: Readonly<Record<ProtocoloDaIa, string>> = {
  openai: 'Compatível com OpenAI (OpenRouter e outros)',
  gemini: 'Gemini, pela API do Google',
  anthropic: 'Anthropic, pela API de mensagens',
};

export const AJUDA_DO_ENDERECO: Readonly<Record<ProtocoloDaIa, string>> = {
  openai: 'A base da API do provedor, com https:// e a versão no fim, sem barra. Está no guia do provedor e no runbook de implantação.',
  gemini: 'A base da API do Google, com a versão no fim (v1beta), sem barra.',
  anthropic: 'O endereço de mensagens da API, completo.',
};

export const ROTULO_DA_ORIGEM: Readonly<Record<OrigemDaIa, string>> = {
  configuracoes: 'Configurações do sistema',
  ambiente: 'Variáveis do servidor',
  nenhuma: 'Nenhuma: a IA está desligada',
};

/** O protocolo da Anthropic, neste sistema, só responde: não lê arquivo nem monta vetor. */
export const soResponde = (protocolo: ProtocoloDaIa): boolean => protocolo === 'anthropic';

/** Quantos modelos a lista mostra de uma vez. O resto se acha escrevendo parte do nome. */
export const LIMITE_VISIVEL = 50;

// ------------------------------------------------------------------ rascunho

/** O que está nos campos. Texto vazio é "sem modelo próprio" nos dois opcionais. */
export interface RascunhoDaIa {
  readonly protocolo: ProtocoloDaIa;
  readonly endereco: string;
  readonly modelo: string;
  readonly modeloArquivo: string;
  readonly modeloVetor: string;
}

export const RASCUNHO_VAZIO: RascunhoDaIa = { protocolo: 'openai', endereco: '', modelo: '', modeloArquivo: '', modeloVetor: '' };

export function rascunhoDe(p: ParametrosDaIa): RascunhoDaIa {
  return {
    protocolo: p.protocolo,
    endereco: p.endereco,
    modelo: p.modelo,
    modeloArquivo: p.modeloArquivo ?? '',
    modeloVetor: p.modeloVetor ?? '',
  };
}

/**
 * De onde o formulário começa: da configuração salva; sem ela, do que a API
 * usa agora (as variáveis do servidor); sem nenhuma das duas, em branco no
 * protocolo compatível com OpenAI, que é o da decisão do dono.
 *
 * O `emUso` traz o modelo de arquivo JÁ RESOLVIDO — no Gemini do ambiente ele
 * é o próprio principal. Igual ao principal volta a ser vazio, que é o jeito
 * de a tela dizer "o mesmo modelo".
 */
export function rascunhoInicial(config: ConfiguracaoDaIaDaPlataforma | null | undefined): RascunhoDaIa {
  if (config?.salva) return rascunhoDe(config.salva);
  const uso = config?.emUso;
  if (!uso?.protocolo) return RASCUNHO_VAZIO;
  return {
    protocolo: uso.protocolo,
    endereco: uso.endereco ?? '',
    modelo: uso.modelo ?? '',
    modeloArquivo: uso.modeloArquivo && uso.modeloArquivo !== uso.modelo ? uso.modeloArquivo : '',
    modeloVetor: uso.modeloVetor ?? '',
  };
}

/**
 * O endereço como o servidor o guarda: sem espaço nas pontas, sem barra no
 * fim, com esquema e nome da máquina em minúscula (o caminho fica como veio).
 */
export function normalizarEndereco(texto: string): string {
  const semBarra = texto.trim().replace(/\/+$/, '');
  const partes = /^([A-Za-z][A-Za-z0-9+.-]*:\/\/)([^/]*)(.*)$/.exec(semBarra);
  if (!partes) return semBarra;
  const [, esquema = '', maquina = '', caminho = ''] = partes;
  return `${esquema.toLowerCase()}${maquina.toLowerCase()}${caminho}`;
}

/* A MESMA REGRA do check da 0042 (e do zod da rota): https, nome público com
   domínio de letras no fim, caminho só com caracteres comuns. Recusa IP v4 e
   v6, porta, usuário e senha, consulta, fragmento, nome de uma parte só e os
   nomes de rede interna. Espelhar aqui poupa a ida ao servidor — o servidor
   continua sendo quem decide. */
const REGRA_DO_ENDERECO =
  /^https:\/\/[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*\.[a-z]{2,}(\/[A-Za-z0-9._~-]+)*$/;
const NOME_INTERNO = /\.(local|localhost|internal|lan|home|corp)(\/|$)/;
export const MAIOR_ENDERECO = 300;

export const ERRO_DO_ENDERECO =
  'Use um endereço com https://, sem porta, sem barra no fim e com nome público. IP e nome interno não valem.';

/** `null` quando o endereço serve; senão, a frase que diz como escrevê-lo. */
export function erroDoEndereco(texto: string): string | null {
  const endereco = normalizarEndereco(texto);
  const serve = endereco.length > 0 && endereco.length <= MAIOR_ENDERECO && REGRA_DO_ENDERECO.test(endereco) && !NOME_INTERNO.test(endereco);
  return serve ? null : ERRO_DO_ENDERECO;
}

/** O identificador do modelo, na regra da rota: aceita `/`, `:`, `.`, `@` e o `~` do começo; nada de espaço. */
export const REGRA_DO_MODELO = /^[~A-Za-z0-9][A-Za-z0-9._:/@~-]{0,199}$/;

const ESCOLHA_O_MODELO = 'Escolha o modelo na lista do provedor, ou escreva o identificador exato.';

export function erroDoModelo(texto: string, opcional: boolean): string | null {
  const id = texto.trim();
  if (id === '') return opcional ? null : ESCOLHA_O_MODELO;
  return REGRA_DO_MODELO.test(id) ? null : `O identificador não tem espaço nem acento. ${ESCOLHA_O_MODELO}`;
}

export interface ErrosDoRascunho {
  readonly endereco: string | null;
  readonly modelo: string | null;
  readonly modeloArquivo: string | null;
  readonly modeloVetor: string | null;
}

export function errosDoRascunho(r: RascunhoDaIa): ErrosDoRascunho {
  const opcionais = !soResponde(r.protocolo);
  return {
    endereco: erroDoEndereco(r.endereco),
    modelo: erroDoModelo(r.modelo, false),
    modeloArquivo: opcionais ? erroDoModelo(r.modeloArquivo, true) : null,
    modeloVetor: opcionais ? erroDoModelo(r.modeloVetor, true) : null,
  };
}

export function rascunhoValido(r: RascunhoDaIa): boolean {
  return Object.values(errosDoRascunho(r)).every((erro) => erro === null);
}

const vazioViraNulo = (texto: string): string | null => (texto.trim() === '' ? null : texto.trim());

/**
 * O rascunho no formato que a rota aceita: sem espaço nas pontas, vazio vira
 * `null`, endereço normalizado. No protocolo da Anthropic os dois opcionais
 * vão sempre nulos — a rota e o banco recusam os dois preenchidos.
 */
export function paraPedido(r: RascunhoDaIa): ParametrosDaIa {
  const opcionais = !soResponde(r.protocolo);
  return {
    protocolo: r.protocolo,
    endereco: normalizarEndereco(r.endereco),
    modelo: r.modelo.trim(),
    modeloArquivo: opcionais ? vazioViraNulo(r.modeloArquivo) : null,
    modeloVetor: opcionais ? vazioViraNulo(r.modeloVetor) : null,
  };
}

function mesmosParametros(a: ParametrosDaIa, b: ParametrosDaIa): boolean {
  return (
    a.protocolo === b.protocolo &&
    a.endereco === b.endereco &&
    a.modelo === b.modelo &&
    a.modeloArquivo === b.modeloArquivo &&
    a.modeloVetor === b.modeloVetor
  );
}

/** O rascunho difere da base no que IRIA para o servidor? Espaço e barra no fim não contam. */
export function mudou(rascunho: RascunhoDaIa, base: RascunhoDaIa): boolean {
  return !mesmosParametros(paraPedido(rascunho), paraPedido(base));
}

/**
 * O que falta no rascunho, em frase que aponta o campo — o botão desligado
 * sem dizer por quê obriga a pessoa a adivinhar. O campo só fica marcado de
 * vermelho depois que ela sai dele; esta frase vale antes disso.
 */
function faltaNoRascunho(r: RascunhoDaIa, antesDe: string): string | null {
  const erros = errosDoRascunho(r);
  if (erros.endereco) return `Preencha um endereço válido antes de ${antesDe}.`;
  if (erros.modelo) return `Escolha o modelo para responder antes de ${antesDe}.`;
  if (erros.modeloArquivo || erros.modeloVetor) return `Corrija o identificador do modelo opcional antes de ${antesDe}.`;
  return null;
}

/**
 * Por que "Salvar configuração" está desligado, ou `null` quando pode salvar.
 * Sem nada salvo, qualquer rascunho válido é mudança: salvar tira a IA das
 * variáveis do servidor e a põe nesta tela.
 */
export function porQueNaoSalva(rascunho: RascunhoDaIa, config: ConfiguracaoDaIaDaPlataforma): string | null {
  const falta = faltaNoRascunho(rascunho, 'salvar');
  if (falta) return falta;
  if (config.salva && !mudou(rascunho, rascunhoDe(config.salva))) return 'Nada mudou desde a configuração salva.';
  return null;
}

/** Por que "Testar configuração" está desligado, ou `null` quando pode testar. */
export function porQueNaoTesta(rascunho: RascunhoDaIa, chaveNoAmbiente: boolean): string | null {
  if (!chaveNoAmbiente) return 'Sem a chave no servidor não há como testar.';
  return faltaNoRascunho(rascunho, 'testar');
}

// ------------------------------------------------------------- IA em uso

/** Um identificador (vai em monoespaçada) ou uma frase. */
export type ValorEmUso = { readonly id: string } | { readonly frase: string };

/** Quem lê os arquivos agora. Sem protocolo em uso não há o que dizer. */
export function leituraEmUso(uso: IaEmUso): ValorEmUso {
  if (uso.modeloArquivo) return { id: uso.modeloArquivo };
  return { frase: uso.protocolo && soResponde(uso.protocolo) ? 'Não lê arquivos neste protocolo' : '—' };
}

/** O modelo do índice por sentido agora. Nulo com protocolo em uso é índice desligado. */
export function indiceEmUso(uso: IaEmUso): ValorEmUso {
  if (uso.modeloVetor) return { id: uso.modeloVetor };
  return { frase: uso.protocolo ? 'Desligado: a busca nos manuais fica só por palavra' : '—' };
}

// ------------------------------------------------------------ lista de modelos

export interface ModelosFiltrados {
  readonly visiveis: readonly ModeloDoProvedor[];
  /** Quantos combinam com o termo, antes do corte em `limite`. */
  readonly encontrados: number;
}

/** Os modelos que têm o termo no id ou no nome, sem diferença de acento nem de caixa, até o limite. */
export function filtrarModelos(modelos: readonly ModeloDoProvedor[], termo: string, limite = LIMITE_VISIVEL): ModelosFiltrados {
  const combinam = modelos.filter((m) => combinaComTermo([m.id, m.nome], termo));
  return { visiveis: combinam.slice(0, limite), encontrados: combinam.length };
}

/** "Mostrando 50 de 312 modelos. …", ou `null` quando a lista inteira cabe. */
export function rodapeDaLista(filtrados: ModelosFiltrados): string | null {
  if (filtrados.encontrados <= filtrados.visiveis.length) return null;
  return `Mostrando ${formatarInteiro(filtrados.visiveis.length)} de ${formatarInteiro(filtrados.encontrados)} modelos. Escreva parte do nome para achar outro.`;
}

const DOLAR = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 });

/**
 * O preço de 1 milhão de tokens, em dólar (é como o provedor cobra).
 *
 * Preço nulo tem dois sentidos, e a lista inteira diz qual: num provedor que
 * informa preço, o modelo sem preço é um ROTEADOR, que cobra o do modelo que
 * escolher ("Variável"); num provedor que não informa preço de nenhum, é só
 * dado ausente ("—").
 */
export function formatarPrecoPorMilhao(valor: number | null, provedorInformaPreco: boolean): string {
  if (valor === null || !Number.isFinite(valor) || valor < 0) return provedorInformaPreco ? 'Variável' : '—';
  return `US$ ${DOLAR.format(valor)}`;
}

/** O provedor informa preço se ao menos um modelo da lista tem preço. */
export const informaPreco = (modelos: readonly ModeloDoProvedor[]): boolean =>
  modelos.some((m) => m.precoEntradaPorMilhao !== null || m.precoSaidaPorMilhao !== null);

/**
 * O preço do item da lista: entrada e saída nos modelos de resposta, só a
 * entrada nos de vetor (vetor não tem saída cobrada). A unidade vai separada,
 * para a tela a escrever em cinza embaixo do número.
 */
export function precoDoModelo(
  m: ModeloDoProvedor,
  tipo: 'resposta' | 'vetor',
  provedorInformaPreco: boolean,
): { readonly valor: string; readonly unidade: string | null } {
  const entrada = formatarPrecoPorMilhao(m.precoEntradaPorMilhao, provedorInformaPreco);
  const saida = formatarPrecoPorMilhao(m.precoSaidaPorMilhao, provedorInformaPreco);
  const comPreco = (texto: string) => texto.startsWith('US$');
  if (tipo === 'vetor' || (entrada === saida && !comPreco(entrada))) {
    return { valor: entrada, unidade: comPreco(entrada) ? 'por 1 mi de tokens' : null };
  }
  return { valor: `${entrada} / ${saida}`, unidade: comPreco(entrada) || comPreco(saida) ? 'por 1 mi de tokens' : null };
}

/* Na ordem em que importam aqui: texto sempre, imagem para o cupom e a folha,
   PDF para o manual. O resto vem depois, e o desconhecido como veio. */
const ROTULO_DA_ENTRADA: Readonly<Record<string, string>> = {
  text: 'texto',
  image: 'imagem',
  file: 'PDF',
  audio: 'áudio',
  video: 'vídeo',
};
const ORDEM_DA_ENTRADA = Object.keys(ROTULO_DA_ENTRADA);

/** `['text','image','file']` vira "texto · imagem · PDF". */
export function rotuloDasEntradas(entradas: readonly string[]): string {
  const conhecidas = ORDEM_DA_ENTRADA.filter((e) => entradas.includes(e)).map((e) => ROTULO_DA_ENTRADA[e] ?? e);
  const outras = entradas.filter((e) => !ORDEM_DA_ENTRADA.includes(e));
  return [...conhecidas, ...outras].join(' · ');
}

/** "1.048.576 tokens", ou `null` quando o provedor não informa. */
export function textoDoContexto(contexto: number | null): string | null {
  return contexto === null || !Number.isFinite(contexto) || contexto <= 0 ? null : `${formatarInteiro(contexto)} tokens`;
}

// ------------------------------------------------------------------- avisos

export interface AvisosDoRascunho {
  readonly modelo: readonly string[];
  readonly modeloArquivo: readonly string[];
  readonly modeloVetor: readonly string[];
}

export const AVISO_DE_TROCA_DE_VETOR =
  'Trocar o modelo de vetores refaz o índice por sentido dos manuais de cada empresa, aos poucos: ao entrar texto novo ou pelo botão da biblioteca. Até lá, a busca por sentido perde os trechos ainda não refeitos.';

const foraDaLista = (id: string) => `O identificador “${id}” não está na lista do provedor. Confira antes de salvar.`;

function expira(m: ModeloDoProvedor): string | null {
  if (!m.expiraEm) return null;
  const quando = new Date(m.expiraEm);
  return Number.isNaN(quando.getTime()) ? null : `Este modelo sai do provedor em ${formatarData(quando)}.`;
}

/** O que falta ao modelo que LÊ os arquivos: PDF para o manual, imagem para o cupom e a folha. */
function avisosDeLeitura(m: ModeloDoProvedor): string[] {
  if (m.entradas.length === 0) return [];
  const avisos: string[] = [];
  if (!m.entradas.includes('file')) avisos.push('Este modelo não lê PDF direto; o provedor pode converter por OCR, com custo à parte.');
  if (!m.entradas.includes('image')) avisos.push('Este modelo não lê imagem: o cupom e a folha escaneada não serão lidos.');
  return avisos;
}

/**
 * O que a lista do provedor permite avisar ANTES de salvar, campo a campo.
 * Aviso não impede salvar: o identificador fora da lista pode ser um modelo
 * novo, e a lista pode ter vindo sem a chave. Quem decide é quem administra,
 * com o teste.
 *
 * Os avisos de leitura vão para o modelo que de fato lê: o de arquivos, se
 * preenchido; senão, o principal.
 */
export function avisosDoRascunho(r: RascunhoDaIa, lista: ListaDeModelos | null, vetorEmUso: string | null): AvisosDoRascunho {
  const p = paraPedido(r);
  const comLista = lista !== null && lista.listaDisponivel;
  const acha = (id: string, modelos: readonly ModeloDoProvedor[]) => modelos.find((m) => m.id === id) ?? null;
  const leArquivo = !soResponde(p.protocolo);

  const modelo: string[] = [];
  const modeloArquivo: string[] = [];
  const modeloVetor: string[] = [];

  if (comLista && p.modelo !== '' && erroDoModelo(p.modelo, false) === null) {
    const m = acha(p.modelo, lista.modelos);
    if (!m) modelo.push(foraDaLista(p.modelo));
    else {
      if (m.respostaEmJson === false) modelo.push('Este modelo não declara resposta em JSON; as histórias de IA exigem JSON.');
      if (leArquivo && p.modeloArquivo === null) modelo.push(...avisosDeLeitura(m));
      const sai = expira(m);
      if (sai) modelo.push(sai);
    }
  }

  if (comLista && p.modeloArquivo !== null && erroDoModelo(p.modeloArquivo, true) === null) {
    const m = acha(p.modeloArquivo, lista.modelos);
    if (!m) modeloArquivo.push(foraDaLista(p.modeloArquivo));
    else {
      modeloArquivo.push(...avisosDeLeitura(m));
      const sai = expira(m);
      if (sai) modeloArquivo.push(sai);
    }
  }

  if (p.modeloVetor !== null && erroDoModelo(p.modeloVetor, true) === null) {
    if (comLista) {
      const m = acha(p.modeloVetor, lista.modelosDeVetor);
      if (!m) modeloVetor.push(foraDaLista(p.modeloVetor));
      else {
        const sai = expira(m);
        if (sai) modeloVetor.push(sai);
      }
    }
    if (p.modeloVetor !== vetorEmUso) modeloVetor.push(AVISO_DE_TROCA_DE_VETOR);
  }

  return { modelo, modeloArquivo, modeloVetor };
}

// -------------------------------------------------------------------- teste

/** O último teste, com os campos EXATOS que foram testados. */
export interface TesteFeito {
  readonly parametros: ParametrosDaIa;
  readonly resultado: ResultadoDoTesteDaIa;
}

/** O teste só vale para os campos como estavam: mudou um, o resultado é de outra configuração. */
export function testeValeParaORascunho(teste: TesteFeito | null, rascunho: RascunhoDaIa): boolean {
  return teste !== null && mesmosParametros(teste.parametros, paraPedido(rascunho));
}

/** Todas as etapas que se aplicam funcionaram. */
export function testeTodoOk(resultado: ResultadoDoTesteDaIa): boolean {
  return resultado.resposta.ok && (resultado.leitura?.ok ?? true) && (resultado.vetores?.ok ?? true);
}

/** Este rascunho, exatamente como está, passou em todas as etapas? */
export const passouNoTeste = (teste: TesteFeito | null, rascunho: RascunhoDaIa): boolean =>
  teste !== null && testeValeParaORascunho(teste, rascunho) && testeTodoOk(teste.resultado);
